import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { HasPermissionDirective } from 'app/shared/directives/has-permission.directive';
import { AutoTranslateDirective } from 'app/shared/directives/auto-translate.directive';
import { AutoTranslatePipe } from 'app/shared/pipes/translate.pipe';
import { TimezoneDatePipe } from 'app/shared/pipes/timezone-date.pipe';
import type { CallRoom } from '../calls-widget/calls-types';
import { Conversation, ConversationContact, ConversationParticipant } from 'app/models/conversation.model';
import { ConversationThreadComponent } from '../conversation-thread/conversation-thread.component';
import { CreateGroupDialogComponent, CreateGroupDialogResult } from './create-group-dialog.component';
import { dialogFormConfig } from 'app/shared/constants/dialog-form.config';

export type CallsSidebarListTab = 'groups' | 'chats';
export type GroupsSubTab = 'services' | 'general';

export interface ContactRoleGroup {
    roleKey: string;
    roleLabel: string;
    contacts: ConversationContact[];
}

export interface ContactCompanyGroup {
    companyKey: string;
    companyName: string;
    roles: ContactRoleGroup[];
}

const PROFILE_LABELS: Record<string, string> = {
    DOCTOR: 'Médico',
    NURSE: 'Enfermeiro',
    NURSING_TECH: 'Técnico de Enfermagem',
    DRIVER: 'Motorista',
    PARAMEDIC: 'Socorrista',
    COMPANY_ADMIN: 'Administrativo',
    ADMIN: 'Admin',
    PATIENT: 'Paciente',
};

@Component({
    selector: 'app-calls-sidebar',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        MatButtonModule,
        MatIconModule,
        MatTooltipModule,
        MatFormFieldModule,
        MatInputModule,
        MatDialogModule,
        HasPermissionDirective,
        AutoTranslateDirective,
        AutoTranslatePipe,
        TimezoneDatePipe,
        ConversationThreadComponent,
    ],
    templateUrl: './calls-sidebar.component.html',
    styleUrls: ['./calls-sidebar.component.scss'],
})
export class CallsSidebarComponent {
    @Input() rooms: CallRoom[] = [];
    @Input() conversations: Conversation[] = [];
    @Input() contacts: ConversationContact[] = [];
    @Input() activeConversation: Conversation | null = null;
    @Input() currentUserId: number | null = null;
    @Input() currentUserName: string | null = null;
    @Input() companyId: number | null = null;
    @Input() docked = false;

    @Output() close = new EventEmitter<void>();
    @Output() enterRoom = new EventEmitter<CallRoom>();
    @Output() openConversation = new EventEmitter<Conversation>();
    @Output() startDirectChat = new EventEmitter<ConversationContact>();
    @Output() enterVideoFromConversation = new EventEmitter<Conversation>();
    @Output() backToList = new EventEmitter<void>();
    @Output() groupCreated = new EventEmitter<Conversation>();
    @Output() groupUpdated = new EventEmitter<Conversation>();
    @Output() groupDeleted = new EventEmitter<number>();

    readonly maxInitials = 4;
    listTab: CallsSidebarListTab = 'groups';
    groupsSubTab: GroupsSubTab = 'services';
    pickingContact = false;
    contactSearch = '';

    constructor(
        private readonly router: Router,
        private readonly dialog: MatDialog,
    ) {}

    get directConversations(): Conversation[] {
        return (this.conversations || [])
            .filter((c) => c.type === 'DIRECT')
            .slice()
            .sort((a, b) => this.timeValue(b.last_message_at) - this.timeValue(a.last_message_at));
    }

    get serviceGroupConversations(): Conversation[] {
        return (this.conversations || [])
            .filter((c) => c.type === 'SERVICE' && c.status !== 'ARCHIVED')
            .slice()
            .sort((a, b) => {
                const byTime = this.timeValue(b.last_message_at || b.createdAt) - this.timeValue(a.last_message_at || a.createdAt);
                if (byTime !== 0) return byTime;
                return (b.service_id || 0) - (a.service_id || 0);
            });
    }

    get generalGroupConversations(): Conversation[] {
        return (this.conversations || [])
            .filter((c) => c.type === 'GROUP' && c.status !== 'ARCHIVED')
            .slice()
            .sort((a, b) => {
                const byTime = this.timeValue(b.last_message_at) - this.timeValue(a.last_message_at);
                if (byTime !== 0) return byTime;
                return (a.display_name || a.name || '').localeCompare(b.display_name || b.name || '', 'pt-BR');
            });
    }

    get extraVideoRooms(): CallRoom[] {
        const conversationRoomIds = new Set(
            (this.conversations || [])
                .map((c) => c.call_room_id)
                .filter((id): id is number => id != null)
                .map((id) => String(id)),
        );
        const conversationSlugs = new Set(
            (this.conversations || []).map((c) => c.call_room_slug).filter((slug): slug is string => !!slug),
        );
        return (this.rooms || []).filter((room) => {
            if (conversationRoomIds.has(String(room.id))) return false;
            if (room.slug && conversationSlugs.has(room.slug)) return false;
            return true;
        });
    }

    get filteredContactGroups(): ContactCompanyGroup[] {
        const query = this.contactSearch.trim().toLocaleLowerCase();
        const contacts = (this.contacts || []).filter((contact) => {
            if (!query) return true;
            const role = this.contactBadge(contact).toLocaleLowerCase();
            return (
                contact.name.toLocaleLowerCase().includes(query) ||
                (contact.company_name || '').toLocaleLowerCase().includes(query) ||
                role.includes(query)
            );
        });
        return this.groupContacts(contacts);
    }

    profileLabel(profileType: string | null | undefined, roleName?: string | null): string {
        if (profileType && PROFILE_LABELS[profileType]) return PROFILE_LABELS[profileType];
        if (roleName?.trim()) return roleName.trim();
        if (profileType) return profileType;
        return 'Sem função';
    }

    contactBadge(contact: ConversationContact): string {
        return this.profileLabel(contact.profile_type, contact.role_name);
    }

    getInitials(name: string | undefined | null): string {
        if (name == null || !String(name).trim()) return '?';
        const s = String(name).trim();
        const parts = s.split(/\s+/).filter(Boolean);
        if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
        return s.slice(0, 2).toUpperCase();
    }

    directPeer(conversation: Conversation): ConversationParticipant | null {
        return (conversation.participants || []).find((p) => p.user_id !== this.currentUserId) ?? null;
    }

    peerContact(conversation: Conversation): ConversationContact | null {
        const peer = this.directPeer(conversation);
        if (!peer) return null;
        return (this.contacts || []).find((c) => c.user_id === peer.user_id) ?? null;
    }

    peerStatus(conversation: Conversation): ConversationContact['status'] | null {
        return this.peerContact(conversation)?.status ?? null;
    }

    peerBadge(conversation: Conversation): string | null {
        const contact = this.peerContact(conversation);
        return contact ? this.contactBadge(contact) : null;
    }

    onClose(): void {
        this.close.emit();
    }

    goToCreateRoom(): void {
        this.onClose();
        this.router.navigate(['/calls'], { queryParams: { create: '1' } });
    }

    goToManageRooms(): void {
        this.onClose();
        this.router.navigate(['/calls']);
    }

    onRoomClick(room: CallRoom): void {
        this.enterRoom.emit(room);
    }

    onConversationClick(conversation: Conversation): void {
        this.pickingContact = false;
        this.openConversation.emit(conversation);
    }

    onContactChat(contact: ConversationContact, event?: Event): void {
        event?.stopPropagation();
        this.pickingContact = false;
        this.contactSearch = '';
        this.startDirectChat.emit(contact);
    }

    openContactPicker(): void {
        this.listTab = 'chats';
        this.pickingContact = true;
        this.contactSearch = '';
    }

    closeContactPicker(): void {
        this.pickingContact = false;
        this.contactSearch = '';
    }

    onBackFromThread(): void {
        this.backToList.emit();
    }

    onEnterVideo(conversation: Conversation): void {
        this.enterVideoFromConversation.emit(conversation);
    }

    openCreateGroup(): void {
        if (!this.companyId) return;
        const ref = this.dialog.open(CreateGroupDialogComponent, {
            ...dialogFormConfig('480px'),
            data: {
                companyId: this.companyId,
                contacts: this.contacts,
            },
        });
        ref.afterClosed().subscribe((created?: Conversation) => {
            if (created) {
                this.listTab = 'groups';
                this.groupsSubTab = 'general';
                this.groupCreated.emit(created);
            }
        });
    }

    canManageGroup(conversation: Conversation | null | undefined): boolean {
        return !!conversation && (conversation.type === 'GROUP' || conversation.type === 'SERVICE');
    }

    openEditGroup(conversation: Conversation, event?: Event): void {
        event?.stopPropagation();
        if (!this.canManageGroup(conversation)) return;
        const companyId = this.companyId ?? conversation.company_id;
        if (!companyId) return;
        const ref = this.dialog.open(CreateGroupDialogComponent, {
            ...dialogFormConfig('480px'),
            data: {
                companyId,
                contacts: this.contacts,
                mode: 'edit',
                conversation,
            },
        });
        ref.afterClosed().subscribe((result?: CreateGroupDialogResult) => {
            if (!result) return;
            if ('deleted' in result && result.deleted) {
                this.groupDeleted.emit(result.conversation_id);
                return;
            }
            this.groupUpdated.emit(result as Conversation);
        });
    }

    lastPreview(c: Conversation): string {
        const m = c.last_message;
        if (!m) return '';
        if (m.type === 'TEXT') return m.content || '';
        if (m.type === 'SYSTEM') return '';
        if (m.type === 'AUDIO') return '🎤 Áudio';
        if (m.type === 'IMAGE') return '📷 Imagem';
        if (m.type === 'FILE') return '📎 Arquivo';
        return '';
    }

    messageCount(c: Conversation): number {
        return c.message_count ?? 0;
    }

    serviceStartedAt(c: Conversation | null | undefined): string | Date | null | undefined {
        if (!c) return null;
        return c.service_start || c.createdAt;
    }

    lastActivityAt(c: Conversation): string | Date | null | undefined {
        return c.last_message_at || c.createdAt;
    }

    relativeAgo(value: string | Date | null | undefined): string {
        const t = this.timeValue(value);
        if (!t) return '';
        const diffMs = Date.now() - t;
        const minutes = Math.max(0, Math.floor(diffMs / 60000));
        if (minutes < 1) return 'agora';
        if (minutes < 60) return `há ${minutes} min`;
        const hours = Math.floor(minutes / 60);
        if (hours < 24) return `há ${hours} h`;
        const days = Math.floor(hours / 24);
        return `há ${days} d`;
    }

    isGroupLike(conversation: Conversation | null): boolean {
        return conversation?.type === 'GROUP' || conversation?.type === 'SERVICE';
    }

    visibleParticipants(room: CallRoom): { name: string; initials: string }[] {
        return room.participants.slice(0, this.maxInitials);
    }

    overflowCount(room: CallRoom): number {
        return Math.max(0, room.participants.length - this.maxInitials);
    }

    private timeValue(value: string | Date | null | undefined): number {
        if (!value) return 0;
        const t = new Date(value).getTime();
        return Number.isFinite(t) ? t : 0;
    }

    private groupContacts(contacts: ConversationContact[]): ContactCompanyGroup[] {
        const companies = new Map<string, { name: string; roles: Map<string, ConversationContact[]> }>();

        for (const contact of contacts) {
            const companyKey = contact.company_id != null ? String(contact.company_id) : 'none';
            const companyName = contact.company_name?.trim() || '';
            const roleKey = contact.profile_type || contact.role_name || 'none';
            if (!companies.has(companyKey)) {
                companies.set(companyKey, { name: companyName, roles: new Map() });
            }
            const company = companies.get(companyKey)!;
            if (!company.roles.has(roleKey)) {
                company.roles.set(roleKey, []);
            }
            company.roles.get(roleKey)!.push(contact);
        }

        return [...companies.entries()]
            .map(([companyKey, company]) => ({
                companyKey,
                companyName: company.name,
                roles: [...company.roles.entries()]
                    .map(([roleKey, roleContacts]) => ({
                        roleKey,
                        roleLabel: this.profileLabel(roleKey === 'none' ? null : roleKey, roleContacts[0]?.role_name),
                        contacts: roleContacts.slice().sort((a, b) => a.name.localeCompare(b.name, 'pt-BR')),
                    }))
                    .sort((a, b) => a.roleLabel.localeCompare(b.roleLabel, 'pt-BR')),
            }))
            .sort((a, b) => a.companyName.localeCompare(b.companyName, 'pt-BR'));
    }
}
